import api from './api';
import { upload } from './upload';

export interface AsrCreateTaskResponse {
  taskIds: string[];
}

export interface AsrTaskResult {
  taskId: string;
  status: 'processing' | 'completed' | 'failed';
  content?: string;
  message?: string;
}

export interface AsrQueryResponse {
  results: AsrTaskResult[];
}

/**
 * 创建 ASR 任务
 */
export const createAsrTask = async (fileUrl: string, engine: string = 'zh-CN') => {
  const response = await api.post<AsrCreateTaskResponse>('/sj/asr/create', {
    fileUrl,
    engine,
  });
  
  const taskIds = response.data?.taskIds;
  if (taskIds && taskIds.length > 0) {
    return taskIds[0];
  }
  throw new Error('Failed to create ASR task: No taskId returned');
};

/**
 * 查询 ASR 任务状态
 */
export const queryAsrTask = async (taskId: string) => {
  const response = await api.post<AsrQueryResponse>('/sj/task/query', {
    taskIds: [taskId],
  });
  
  const results = response.data?.results;
  if (results && results.length > 0) {
    return results[0];
  }
  throw new Error('Failed to query ASR task');
};

/**
 * 执行 ASR (上传 -> 创建任务 -> 轮询)
 */
export const performAsr = async (
  file: File, 
  language: string = 'zh-CN',
  onUploadProgress?: (progress: number) => void
): Promise<string> => {
  // 1. 上传文件
  const uploadResult = await upload({
    file,
    onProgress: onUploadProgress,
  });
  
  if (!uploadResult.fileUri) {
    throw new Error('File upload failed: No fileUri returned');
  }

  // 2. 创建任务
  const taskId = await createAsrTask(uploadResult.fileUri, language);

  // 3. 轮询结果
  const pollInterval = 2000;
  const timeout = 2 * 60 * 1000; // 2 minutes
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const result = await queryAsrTask(taskId);
    
    if (result.status === 'completed') {
      return result.content || '';
    }
    
    // pending or running, wait and retry
    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }

  throw new Error('ASR task timed out');
};
